import { FormEvent, useContext, useState } from 'react';

import FileUploadApi from '../../../../../api/fileUploadApi';
import IconButton from '../../../../../components/atoms/button/IconButton';
import DragAndDropButton from '../../../../../components/atoms/button-drag-drop';
import Input from '../../../../../components/atoms/input';
import TextArea from '../../../../../components/atoms/text-area/TextArea';
import VideoPlayer from '../../../../../components/molecules/video-player/VideoPlayer';
import { VideoContentData, VideoContentSpecificData } from '../../../../../types/contentTypes';
import { Marker } from '../../../../../types/iMediaPlayer';
import AlertContext from '../../../../../utils/AlertContext';
import { useFileUpload } from '../../../../../utils/hooks';
import { ContentFormProps } from '../../ContentEditor';

import './AddMediaForm.scss';

// Max video file size in MB
const MAXIMUM_VIDEO_SIZE = 100;
const ALLOWED_VIDEO_TYPES = ['mp4', 'webm', 'mkv', 'avi', 'ogm'];

export default function VideoContentForm({
  content,
  onExistingContentSubmit,
  onNewContentSubmit,
}: ContentFormProps<VideoContentData, VideoContentSpecificData>) {
  const [url, setUrl] = useState<string>(content?.url || '');
  const [markers] = useState<Marker[]>(content?.markers || []);
  const [description, setDescription] = useState<string>(content?.description || '');
  const [headline, setHeadline] = useState<string>(content?.headline || '');
  const [storagePath, setStoragePath] = useState<string>(content?.storagePath || '');

  // Non-persistent states, values of states below are temporary and not saved to database.
  const [videoToUpload, setVideoToUpload] = useState<File | 'DELETE_ONLY' | undefined>();
  const { previewUrl, previewFilename, updateStorage } = useFileUpload(
    videoToUpload,
    content?.storagePath || '',
    [storagePath, setStoragePath],
    'videos'
  );

  const showAlert = useContext(AlertContext);

  async function deleteCurrentVideoInStorage() {
    if (content?.storagePath) {
      await FileUploadApi.deleteFile(content.storagePath);
      setStoragePath('');
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (url) {
      await handleUrlVideoSubmit();
    } else {
      await handleFileVideoSubmit();
    }
    showAlert('Changes saved!');
  }

  async function handleFileVideoSubmit() {
    await updateStorage();
    if (!content) {
      onNewContentSubmit?.({
        description,
        markers,
        storagePath,
        headline,
      });
    } else {
      const { url: previousUrl, ...rest } = content;
      onExistingContentSubmit?.({
        ...rest,
        description,
        markers,
        storagePath,
        headline,
      });
    }
  }

  async function handleUrlVideoSubmit() {
    await deleteCurrentVideoInStorage();
    if (!content) {
      onNewContentSubmit?.({
        description,
        markers,
        url,
        headline,
      });
    } else {
      const { storagePath: previousStoragePath, ...rest } = content;
      onExistingContentSubmit?.({
        ...rest,
        description,
        markers,
        url,
        headline,
      });
    }
  }

  function handleClearVideoToUpload() {
    setVideoToUpload('DELETE_ONLY');
  }

  return (
    <form className="video-content-form" onSubmit={handleSubmit}>
      <label className="block mb-4 font-medium">Video URL</label>
      <Input
        className="mb-4 w-full"
        disabled={!!videoToUpload}
        state={[url, setUrl]}
        type="text"
        placeholder="Enter URL for video"
      />
      <p className="mb-4 font-medium">Or</p>
      <DragAndDropButton
        className={`${previewUrl ? 'mb-2' : 'mb-8'}`}
        disabled={!!url}
        setFile={setVideoToUpload}
        maxFileSize={MAXIMUM_VIDEO_SIZE}
        allowedFileTypes={ALLOWED_VIDEO_TYPES}
      />
      {previewUrl && (
        <div className="flex items-center mb-8">
          {previewFilename && <p>{previewFilename}</p>}
          <IconButton
            className="text-red"
            icon="close"
            visualType="no-bg"
            iconSize="sm"
            iconColor="red"
            onClick={handleClearVideoToUpload}
          />
        </div>
      )}
      <label className="block mb-4 font-medium">Video headline</label>
      <Input className="mb-8 w-full" state={[headline, setHeadline]} type="text" placeholder="Enter video headline" />
      <label className="block mb-4 font-medium">Video description</label>
      <TextArea
        className={`w-full mb-${previewUrl ? '8' : '12'}`}
        state={[description, setDescription]}
        placeholder="Enter a description of the video"
        rows={4}
      />
      {previewUrl && <VideoPlayer className="mx-auto mb-12" url={previewUrl} />}
      <IconButton icon="save-outline" label={!content ? 'Save and add to section' : 'Save changes'} type="submit" />
    </form>
  );
}
